/**
 * Ako pevné je heslo, ktoré si človek práve vymýšľa.
 *
 * Server žiada osem znakov a nič viac — čo splní aj `12345678`. Toto nie je
 * ďalšia podmienka a nič nezakazuje; je to spätná väzba počas písania, aby
 * bolo vidieť rozdiel medzi "prejde" a "je to naozaj heslo".
 *
 * Zámerne bez knižnice a bez slovníka. Odhad entropie z reálneho slovníka by
 * do balíka pridal stovky kilobajtov kvôli obrazovke, ktorú človek uvidí raz —
 * a na "toto je krátke a samé číslice" žiadny slovník netreba.
 */
export type PasswordStrength = {
  /** 0 = nič, 4 = pevné. */
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  /** Čo najviac pomôže, keď je heslo slabé. Prázdne, keď je v poriadku. */
  hint: string;
};

/** Minimum, ktoré vyžaduje aj server. */
export const MIN_PASSWORD_LENGTH = 8;

/** Postupnosti, ktoré vyzerajú ako heslo a nie sú ním. */
const OBVIOUS = [
  '12345678',
  '123456789',
  'password',
  'heslo',
  'qwerty',
  'querty',
  'admin',
  'onvi',
  'whoworks',
];

const LABELS: Record<PasswordStrength['score'], string> = {
  0: 'Prislabé',
  1: 'Slabé',
  2: 'Ujde',
  3: 'Dobré',
  4: 'Pevné',
};

export const passwordStrength = (password: string): PasswordStrength => {
  if (!password) {
    return { score: 0, label: LABELS[0], hint: '' };
  }

  const lower = password.toLocaleLowerCase();

  // Známy tvar hesla nezachráni ani dĺžka: `password123456` je stále to prvé,
  // čo ktokoľvek skúsi.
  if (OBVIOUS.some((needle) => lower.includes(needle))) {
    return {
      score: 0,
      label: LABELS[0],
      hint: 'Toto je jedno z prvých hesiel, ktoré ktokoľvek skúsi.',
    };
  }

  if (password.length < MIN_PASSWORD_LENGTH) {
    return {
      score: 0,
      label: LABELS[0],
      hint: `Aspoň ${MIN_PASSWORD_LENGTH} znakov.`,
    };
  }

  // Jeden znak dokola nie je dĺžka.
  if (new Set(password).size <= 2) {
    return { score: 0, label: LABELS[0], hint: 'Skús viac rôznych znakov.' };
  }

  let score = 0;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;

  const kinds = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((re) =>
    re.test(password),
  ).length;
  if (kinds >= 2) score += 1;
  if (kinds >= 3) score += 1;

  const capped = Math.min(4, Math.max(1, score)) as PasswordStrength['score'];

  return {
    score: capped,
    label: LABELS[capped],
    hint:
      capped >= 3
        ? ''
        : password.length < 12
          ? 'Dlhšie heslo pomôže viac než zložitejšie.'
          : 'Skús pridať veľké písmeno, číslicu alebo znak navyše.',
  };
};
